import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ArrowBackRounded, AssignmentRounded, DownloadOutlined } from "@mui/icons-material";
import MainLayout from "../../../layouts/main/main_layout";
import PdfIcon from "../../../assets/pdf_icon.svg";
import XlsIcon from "../../../assets/xls_icon.svg";
import { useQuery } from "react-query";
import {
  downloadEnquiry,
  getEnquiriesById,
} from "../../../services/schemes/schemes";
import { useNavigate, useParams } from "react-router-dom";
import { downloadFromBlob } from "../../../utils/utils";
import Loading from "../../../components/loading/loading";
import AttachmentIcon from "../../../assets/attachment.svg";
import { amberLight, black, greenLight, greyEnabled, purple, purpleLight } from "../../../theme/colors";
import dayjs from "dayjs";
import locales from "../../../constants/locales";
import SendEnquiryNotification from "./components/send_enquiry_notification";
import { useFeedback } from "../../../providers/feedback/feeedback";

export default function EnquiryDetails() {
  let { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [downloadingFormat, setDownloadingFormat] = useState<"pdf" | "excel" | null>(null);

  const { data: schemeDetails, isLoading } = useQuery(
    ["enquiry", id],
    async () => {
      let payload = {
        type: "user",
      };
      let response = await getEnquiriesById(id!, payload);
      return response.data.data;
    },
    {
      enabled: !!id,
      retry: false,
    }
  );

  const preferredLanguageName =
    locales.find((locale) => locale.code === schemeDetails?.preferredLanguage)
      ?.name || schemeDetails?.preferredLanguage;

  const enquiredOnFormatted = schemeDetails?.enquiredOn
    ? dayjs(schemeDetails.enquiredOn).format("DD MMM YYYY")
    : "";

  const sentNotification = schemeDetails?.notifications?.find(
    (notification: any) => notification.status === "Success"
  );

  const downloadEnquiryApiCall = (format: "pdf" | "excel") => {
    setDownloadingFormat(format);
    downloadEnquiry(id!, format, "user")
      .then((res) => {
        let extension = format === "pdf" ? ".pdf" : ".xlsx";
        downloadFromBlob(res, extension);
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Something went wrong while generating the report."
        );
      })
      .finally(() => setDownloadingFormat(null));
  };

  function divider() {
    return (
      <Divider
        sx={{
          display: "flex",
          opacity: 0.6,
          borderBottom: "1px dotted #ABA0AB",
        }}
      />
    );
  }

  function listItem(title: string, value: any) {
    return (
      <Box style={{ overflow: "hidden" }}>
        <Typography
          variant="body2"
          sx={{ color: black, fontWeight: 600, letterSpacing: 0.2 }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ color: black, fontWeight: 500, mt: 0.25 }}
          textOverflow="ellipsis"
          overflow="hidden"
        >
          {value || "-"}
        </Typography>
      </Box>
    );
  }

  const itemList = (
    title1: string,
    title2: string,
    subtitle1: string,
    subtitle2: string
  ) => {
    return (
      <Grid display="flex" flexDirection="row" container spacing={3} mt={0.5}>
        {title1 && (
          <Grid item md={6} sm={6} xs={12}>
            {listItem(title1, subtitle1)}
          </Grid>
        )}
        {title2 && (
          <Grid item md={6} sm={6} xs={12}>
            {listItem(title2, subtitle2)}
          </Grid>
        )}
      </Grid>
    );
  };

  function downloadIcon(
    format: "pdf" | "excel",
    imgSrc: string,
    backgroundColor: string,
    tooltip: string
  ) {
    const isThisSubmitting = downloadingFormat === format;
    return (
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            disabled={downloadingFormat !== null}
            onClick={() => downloadEnquiryApiCall(format)}
            sx={{
              bgcolor: backgroundColor,
              borderRadius: 2,
              ml: 1.5,
              height: 36,
              width: 36,
            }}
          >
            {isThisSubmitting ? (
              <CircularProgress size={18} sx={{ color: purple }} />
            ) : (
              <img src={imgSrc} alt="" style={{ height: 20, width: 20 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <MainLayout>
      <Grid container mt={4} spacing={3}>
        <Grid item lg={8} md={12} sm={12} xs={12} xl={8}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackRounded />
            </IconButton>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: purpleLight,
                  flexShrink: 0,
                }}
              >
                <AssignmentRounded sx={{ color: purple, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color={black}>
                  Enquiry Details
                </Typography>
                <Typography variant="body2" color={greyEnabled}>
                  {schemeDetails?.userName || "View enquiry submission details."}
                </Typography>
              </Box>
            </Box>
          </Box>

          {isLoading ? (
            <Card
              elevation={0}
              sx={{
                display: "flex",
                minHeight: 500,
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 3,
                border: "1px solid #ECECEC",
              }}
            >
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            </Card>
          ) : (
            <Card
              elevation={0}
              sx={{ borderRadius: 3, border: "1px solid #ECECEC" }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                {itemList(
                  "Reference number",
                  "Full Name",
                  schemeDetails?.enquiryId,
                  schemeDetails?.userName
                )}
                {itemList(
                  "Contact Number",
                  "State",
                  schemeDetails?.mobileNumber,
                  schemeDetails?.state
                )}
                {itemList(
                  "District",
                  "Preferred Language",
                  schemeDetails?.district,
                  preferredLanguageName
                )}
                {itemList("Enquired On", "", enquiredOnFormatted, "")}

                <Box my={3}>{divider()}</Box>

                {itemList(
                  "Scheme Enquired",
                  "Government",
                  schemeDetails?.name,
                  schemeDetails?.governmentType
                )}

                <Box mt={3}>
                  <Typography
                    variant="body2"
                    sx={{ color: black, fontWeight: 600, letterSpacing: 0.2 }}
                  >
                    Supporting Documents
                  </Typography>
                  <List dense sx={{ mt: 0.5 }}>
                    {schemeDetails?.uploadedDocuments?.map(
                      (
                        document: { file: string; fileName: string },
                        index: number
                      ) => {
                        return (
                          <ListItem
                            key={document.fileName + index}
                            sx={{
                              border: `1px solid ${purpleLight}`,
                              borderRadius: 2,
                              mb: 1,
                            }}
                          >
                            <ListItemIcon>
                              <img src={AttachmentIcon} alt="" />
                            </ListItemIcon>
                            <ListItemText primary="Document" />
                            <ListItemIcon>
                              <a
                                href={"data:image/png;base64," + document?.file}
                                target="_blank"
                                download={document.fileName}
                                rel="noreferrer"
                              >
                                <IconButton sx={{ color: purple }}>
                                  <DownloadOutlined />
                                </IconButton>
                              </a>
                            </ListItemIcon>
                          </ListItem>
                        );
                      }
                    )}
                  </List>
                </Box>

                <Box mt={3}>
                  <Typography
                    variant="body2"
                    sx={{ color: black, fontWeight: 600, letterSpacing: 0.2 }}
                  >
                    Categories
                  </Typography>
                  <Box display="flex" flexDirection="row" flexWrap="wrap" mt={1} gap={1}>
                    {schemeDetails?.categories?.map((item: any) => {
                      return (
                        <Chip
                          key={item}
                          label={item}
                          size="small"
                          sx={{
                            bgcolor: purpleLight,
                            color: purple,
                            fontWeight: 600,
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                <Box mt={4}>{divider()}</Box>

                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  mt={3}
                  gap={1.5}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Button
                    onClick={() => navigate(-1)}
                    variant="outlined"
                    startIcon={<ArrowBackRounded sx={{ height: 18, width: 18 }} />}
                    sx={{
                      borderColor: purple,
                      color: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: purple, bgcolor: purpleLight },
                    }}
                  >
                    Back To Enquiries
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate("/schemes")}
                    sx={{
                      color: purple,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    View All Scheme details
                  </Button>
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={600}>
                      Generate Report:
                    </Typography>
                    {downloadIcon("pdf", PdfIcon, amberLight, "Download as PDF")}
                    {downloadIcon("excel", XlsIcon, greenLight, "Download as XLS")}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
        {!isLoading && (
          <Grid item xs={12}>
            <SendEnquiryNotification
              enquiryId={id!}
              enquiryType="user"
              sentNotification={sentNotification}
            />
          </Grid>
        )}
      </Grid>
    </MainLayout>
  );
}
